import { Link, useParams } from "react-router-dom";
import { toLocalDisplay } from "../../utils/datetimeLocal";
import {
  ArrowLeft, RefreshCw, FileText, Activity, BookOpen,
  History, Calendar, Lock, Unlock, CheckCircle2, Info, X,
  Loader2, GraduationCap, Pencil, Star,
} from "lucide-react";
import { useState } from "react";
import { useAdminGetExamQuery, useAdminGetExamAnalyticsQuery, useAdminGetDietsByExamQuery } from "../../api/examsApi";
import { useAdminGetCoursesByExamQuery } from "../../api/adminExamCoursesApi";

const STATUS_COLORS: Record<string, string> = {
  open:     "bg-emerald-50 text-emerald-700 border-emerald-100",
  draft:    "bg-gray-50 text-gray-400 border-gray-100",
  closed:   "bg-rose-50 text-rose-700 border-rose-100",
  archived: "bg-amber-50 text-amber-700 border-amber-100",
};

export default function ChiefExamDetailsPage() {
  const { id = "" } = useParams();

  const { data: exam, isLoading, refetch, isFetching }          = useAdminGetExamQuery(id, { skip: !id });
  const { data: analytics, refetch: refetchAnalytics }           = useAdminGetExamAnalyticsQuery({ examId: id } as any, { skip: !id });
  const { data: diets = [], refetch: refetchDiets }              = useAdminGetDietsByExamQuery(id, { skip: !id });
  const { data: courses = [], refetch: refetchCourses }          = useAdminGetCoursesByExamQuery(id, { skip: !id });

  const [toast, setToast] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const handleRefresh = () => {
    refetch(); refetchAnalytics(); refetchDiets(); refetchCourses();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-gray-200" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-4">
          <Link
            to="/exams/chief/exams"
            className="flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors text-[10px] font-black uppercase tracking-widest"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Exams
          </Link>
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-[2rem] bg-gray-900 flex items-center justify-center text-white shadow-2xl">
              <FileText className="w-8 h-8" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase leading-none">
                  {exam?.title ?? "Exam Spec"}
                </h1>
                <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                  exam?.is_active
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : "bg-gray-50 text-gray-400 border-gray-100"
                }`}>
                  {exam?.is_active ? "Live" : "Inactive"}
                </span>
              </div>
              <p className="text-gray-400 text-xs mt-2 font-bold uppercase tracking-widest">
                {exam?.code}
                {exam?.requires_logbook_stage && exam.requires_logbook_stage !== "none"
                  ? ` · Requires ${exam.requires_logbook_stage}`
                  : " · No Logbook Requirement"}
                {" "}· {(courses as any[]).length} Course{(courses as any[]).length !== 1 ? "s" : ""}
                {" "}· {(diets as any[]).length} Diet{(diets as any[]).length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleRefresh}
          className="p-3.5 rounded-2xl border-2 border-gray-100 hover:bg-gray-50 transition-all self-start md:self-auto"
        >
          <RefreshCw className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`p-4 rounded-2xl border flex items-center justify-between animate-in slide-in-from-top-2 ${
          toast.type === "success"
            ? "bg-gray-900 border-gray-800 text-white"
            : "bg-rose-50 border-rose-100 text-rose-900"
        }`}>
          <span className="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
            {toast.type === "success"
              ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              : <Info className="w-4 h-4" />}
            {toast.text}
          </span>
          <button onClick={() => setToast(null)}><X className="w-4 h-4 opacity-50 hover:opacity-100" /></button>
        </div>
      )}

      {/* ── Analytics ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-100 p-8 rounded-[2.5rem] shadow-sm">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
            Total Applications
          </div>
          <div className="text-5xl font-black text-gray-900 tracking-tighter">
            {(analytics as any)?.totals?.applications ?? 0}
          </div>
        </div>

        <div className="bg-white border border-gray-100 p-8 rounded-[2.5rem] shadow-sm md:col-span-3">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">
            Application Status Breakdown
          </div>
          {Object.keys((analytics as any)?.by_status ?? {}).length === 0 ? (
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">No applications yet</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries((analytics as any)?.by_status ?? {}).map(([k, v]: any) => (
                <div key={k} className="bg-gray-50 px-4 py-2 rounded-xl border border-gray-100 text-[10px] font-black uppercase tracking-tighter">
                  <span className="text-gray-400 mr-2">{k}:</span>{v}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Paid",      val: (analytics as any)?.by_status?.paid      ?? 0, color: "emerald" },
            { label: "Approved",  val: (analytics as any)?.by_status?.approved  ?? 0, color: "indigo"  },
            { label: "Rejected",  val: (analytics as any)?.by_status?.rejected  ?? 0, color: "rose"    },
            { label: "Completed", val: (analytics as any)?.by_status?.completed ?? 0, color: "amber"   },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-[2rem] p-6 shadow-sm flex items-center gap-4">
              <div className={`w-10 h-10 rounded-2xl bg-${s.color}-50 flex items-center justify-center shrink-0`}>
                <Activity className={`w-5 h-5 text-${s.color}-500`} />
              </div>
              <div>
                <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{s.label}</div>
                <div className="text-2xl font-black text-gray-900 tracking-tighter">{s.val}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Exam Diets ───────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="p-8 border-b bg-gray-50/50 flex items-center gap-3">
          <History className="w-5 h-5 text-indigo-600" />
          <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">
            Exam Diets ({(diets as any[]).length})
          </h2>
        </div>

        {(diets as any[]).length === 0 ? (
          <div className="py-20 text-center">
            <Calendar className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
              No diets configured yet
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(diets as any[]).map((d: any) => (
              <div key={d.id} className="flex items-center gap-4 px-8 py-5 hover:bg-gray-50/80 transition-all">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-black text-gray-900 uppercase tracking-tight">{d.title}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                      STATUS_COLORS[d.status] ?? "bg-gray-50 text-gray-400 border-gray-100"
                    }`}>
                      {d.status}
                    </span>
                    {d.is_active && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-indigo-50 text-indigo-600 border-indigo-100">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 flex-wrap">
                    {d.applications_open_at && (
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                        Open: {toLocalDisplay(d.applications_open_at)}
                      </span>
                    )}
                    {d.applications_close_at && (
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                        Close: {toLocalDisplay(d.applications_close_at)}
                      </span>
                    )}
                  </div>
                </div>
                {/* Assign examiners to courses — the Chief Examiner assigns, does NOT grade */}
                <div className="flex items-center gap-3 shrink-0">
                  <Link
                    to={`/exams/chief/diets/${d.id}/assign-courses`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                  >
                    <Pencil className="w-3 h-3" />
                    Assign Examiners
                  </Link>
                  <Link
                    to={`/exams/chief/diets/${d.id}/grading`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 transition-all"
                  >
                    <Star className="w-3 h-3" />
                    Grade / Override
                  </Link>
                  {d.status === "open" ? (
                    <Unlock className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-200" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Courses ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="p-8 border-b bg-gray-50/50 flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">
            Courses ({(courses as any[]).length})
          </h2>
        </div>

        {(courses as any[]).length === 0 ? (
          <div className="py-20 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
              No courses added yet
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(courses as any[]).map((c: any) => (
              <div key={c.id} className="flex items-center gap-4 px-8 py-5 hover:bg-gray-50/80 transition-all">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-black text-[10px] text-indigo-600 uppercase tracking-widest">{c.code}</span>
                    <span className="font-black text-gray-900 uppercase tracking-tight text-sm">{c.name}</span>
                    {c.has_multiple_papers && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-100">
                        Multi-Paper
                      </span>
                    )}
                    {!c.is_active && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-gray-50 text-gray-400 border border-gray-100">
                        Inactive
                      </span>
                    )}
                  </div>
                  {c.description && (
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 truncate max-w-md">
                      {c.description}
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  {c.is_active
                    ? <Unlock className="w-4 h-4 text-emerald-400" />
                    : <Lock className="w-4 h-4 text-gray-200" />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Logbook requirement note ─────────────────────────────────── */}
      {exam?.requires_logbook_stage && exam.requires_logbook_stage !== "none" && (
        <div className="bg-amber-50 border border-amber-100 rounded-3xl p-6 flex items-center gap-4">
          <GraduationCap className="w-6 h-6 text-amber-500 shrink-0" />
          <p className="text-sm text-amber-700 font-bold">
            This exam requires candidates to have completed{" "}
            <strong>{exam.requires_logbook_stage.replace("stage", "Stage ")}</strong>{" "}
            of their logbook before applying.
          </p>
        </div>
      )}

    </div>
  );
}
